import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import { VariantAsset } from "@/types/models/order";

export const generateImageSrc = (
  suffix: string,
  width: number = 600,
  height: number = 400,
) => {
  suffix = suffix.startsWith("/") ? suffix.slice(1) : suffix;
  suffix = suffix.endsWith("/") ? suffix.slice(0, -1) : suffix;
  if (!suffix) {
    console.warn("No image suffix provided, returning empty string.");
    return "";
  }

  // Use the new getAsset function from SDK
  const url = getAsset(suffix, {
    width,
    height,
    fit: 'inside'
  });

  console.log("Generated image URL:", url);
  return url;
};

/**
 * Get the first variant asset image URL using weserv.nl based mapping
 * @param assets - Array of variant assets
 * @param width - Image width (default 100)
 * @param height - Image height (default 100)
 * @returns Image URL string or empty string
 */
export const getVariantImageUrl = (
  assets?: VariantAsset[],
  width: number = 100,
  height: number = 100
): string => {
  if (!assets || assets.length === 0) {
    return "";
  }

  // Sort by position to get the first image
  const sortedAssets = [...assets].sort((a, b) => {
    const posA = a.position ?? Infinity;
    const posB = b.position ?? Infinity;
    return posA - posB;
  });

  // Get the first asset with image usage
  const firstAsset = sortedAssets.find(
    (a) => a.usage === "image" || a.asset?.usage === "general"
  );

  if (firstAsset?.asset?.path) {
    return getAsset(firstAsset.asset.path, {
      width,
      height,
      fit: 'cover'
    });
  }

  // Fallback to first asset if no image usage found
  if (sortedAssets[0]?.asset?.path) {
    return getAsset(sortedAssets[0].asset.path, {
      width,
      height,
      fit: 'cover'
    });
  }

  return "";
};

/**
 * Validates that a string is in slug format (ASCII with dashes, no spaces)
 * @param value - The string to validate
 * @returns true if valid slug format, false otherwise
 */
export const isValidSlug = (value: string): boolean => {
  if (!value) return false;

  // Check for slug pattern: lowercase letters, numbers, and hyphens only
  // Must start and end with alphanumeric characters
  const slugPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

  return slugPattern.test(value);
};

/**
 * Converts a string to slug format
 * @param value - The string to convert
 * @returns slug formatted string
 */
export const toSlug = (value: string): string => {
  return value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, "") // Remove non-alphanumeric characters except hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};
